import { insertIf } from '@app/src/utils/helpersTs'
import { useCallback } from 'react'

const getCSSPath = (element: Element): string => {
  const path = []
  let current = element

  while (current?.nodeType === Node.ELEMENT_NODE) {
    const tag = current.nodeName.toLowerCase()
    const selector = current.id ? `${tag}#${current.id}` : tag

    if (current.id) {
      path.unshift(selector)
      break
    }

    const siblings = Array.from(current.parentElement?.children || [])
    const currentNodeName = current.nodeName
    const index = siblings.filter(s => s.nodeName === currentNodeName).indexOf(current) + 1
    path.unshift(index > 1 ? `${selector}:nth-child(${index})` : selector)
    current = current.parentElement as Element
  }

  return path.join(' > ')
}

/**
 * Hook for preserving scroll position during operations that might cause re-renders
 */
const usePreserveScrollPosition = () => {
  const findAllScrollableElements = useCallback(() => {
    const elements = Array.from(document.querySelectorAll('*')).flatMap(el => {
      const style = getComputedStyle(el)
      if (
        !(
          (['auto', 'scroll'].includes(style.overflow) || ['auto', 'scroll'].includes(style.overflowY)) &&
          el.scrollTop > 0
        )
      )
        return []

      return {
        element: el,
        path: getCSSPath(el),
        scrollTop: el.scrollTop,
      }
    })

    return [
      ...elements,
      ...insertIf(window.scrollY > 0, {
        element: window,
        path: null,
        scrollTop: window.scrollY,
      }),
    ]
  }, [])

  const preserveScrollPosition = useCallback(
    async (callback: () => void | Promise<void>) => {
      const scrollableElements = findAllScrollableElements()

      await callback()

      if (scrollableElements.length > 0) {
        requestAnimationFrame(() => {
          scrollableElements.forEach(({ element, path, scrollTop }) => {
            if (element === window) {
              window.scrollTo(0, scrollTop)
            } else if (path) {
              const foundElement = document.querySelector(path)
              if (foundElement) (foundElement as HTMLElement).scrollTop = scrollTop
            }
          })
        })
      }
    },
    [findAllScrollableElements],
  )

  return preserveScrollPosition
}

export default usePreserveScrollPosition
